import styled from '@emotion/styled';
import * as _ from 'lodash';
import * as React from 'react';
import { useActiveHeadingOnScroll } from '../hooks/useActiveHeadingOnScroll';
import { getHeadingText } from '../utils/getHeadingText';
import { DOCS_RAIL_STICKY_TOP } from '../vars';
import { RailSurfaceWidget } from './RailSurfaceWidget';
import { TreeListItem, TreeListTone, TreeListWidget } from './TreeListWidget';

export interface TableOfContentsItem {
  depth: 2 | 3;
  text: string;
}

export interface TableOfContentsWidgetProps {
  className?: string;
  items: TableOfContentsItem[];
  contentRef: React.RefObject<HTMLElement>;
  scrollContainerRef: React.RefObject<HTMLElement>;
  showHeadingTags?: boolean;
}

export const TableOfContentsWidget: React.FC<TableOfContentsWidgetProps> = (props) => {
  const listRef = React.useRef<HTMLDivElement>(null);
  const headingByKeyRef = React.useRef(new Map<string, HTMLHeadingElement>());
  const keyByHeadingRef = React.useRef(new Map<HTMLHeadingElement, string>());
  const pendingItemKeyRef = React.useRef<string | null>(null);
  const [activeItemKey, setActiveItemKey] = React.useState<string | null>(null);
  const [headingByItemKey, setHeadingByItemKey] = React.useState<
    Record<string, { label: string; tag?: string; tagVersion?: string }>
  >({});
  const setActiveItemKeyDebouncedRef = React.useRef(_.debounce((key: string) => setActiveItemKey(key), 50));

  React.useEffect(() => {
    return () => {
      setActiveItemKeyDebouncedRef.current.cancel();
    };
  }, []);

  const rebuildHeadingElements = React.useCallback(() => {
    const headingByKey = new Map<string, HTMLHeadingElement>();
    const keyByHeading = new Map<HTMLHeadingElement, string>();
    const nextHeadingByItemKey: Record<string, { label: string; tag?: string; tagVersion?: string }> = {};
    const headings = Array.from(props.contentRef.current?.querySelectorAll<HTMLHeadingElement>('h2, h3') ?? []);
    const comparableItems = props.items.map((item, index) => ({
      index,
      item,
      text: getComparableHeadingText(item.text)
    }));
    let itemIndex = 0;

    for (const heading of headings) {
      const headingText = getComparableHeadingText(getHeadingText(heading));
      const fullHeadingText = getComparableHeadingText(heading.innerText);
      const matchedItem = comparableItems.find(
        (item) => item.index >= itemIndex && (item.text === headingText || item.text === fullHeadingText)
      );

      if (matchedItem) {
        const key = getItemKey(matchedItem.item, matchedItem.index);
        headingByKey.set(key, heading);
        keyByHeading.set(heading, key);
        const badge = heading.querySelector<HTMLElement>('[data-tag-badge="true"]');
        nextHeadingByItemKey[key] = {
          label: getHeadingText(heading),
          tag: (badge?.innerText || badge?.textContent || '').trim() || undefined,
          tagVersion: badge?.dataset.sinceVersion || undefined
        };
        itemIndex = matchedItem.index + 1;
      }
    }

    headingByKeyRef.current = headingByKey;
    keyByHeadingRef.current = keyByHeading;
    setHeadingByItemKey(nextHeadingByItemKey);
  }, [props.contentRef, props.items]);

  React.useLayoutEffect(() => {
    rebuildHeadingElements();
  }, [rebuildHeadingElements]);

  React.useEffect(() => {
    pendingItemKeyRef.current = null;
    setActiveItemKeyDebouncedRef.current.cancel();
    setActiveItemKey(null);
    rebuildHeadingElements();
  }, [props.items, rebuildHeadingElements]);

  const getHeadingKey = React.useCallback(
    (heading: HTMLHeadingElement) => keyByHeadingRef.current.get(heading) ?? null,
    []
  );

  useActiveHeadingOnScroll({
    forwardRef: props.contentRef,
    getHeadingKey,
    scrollContainerRef: props.scrollContainerRef,
    onActiveHeading: (heading, element) => {
      if (!heading || !element) {
        return;
      }

      const key = getHeadingKey(element);
      if (key) {
        setActiveItemKeyDebouncedRef.current(key);
      }
    }
  });

  const scrollItemIntoView = (button: HTMLButtonElement | null) => {
    if (!button) {
      return;
    }

    button.scrollIntoView({
      block: 'nearest'
    });
  };

  React.useEffect(() => {
    if (activeItemKey === null || !listRef.current) {
      return;
    }

    if (pendingItemKeyRef.current !== null && pendingItemKeyRef.current !== activeItemKey) {
      return;
    }

    if (pendingItemKeyRef.current === activeItemKey) {
      pendingItemKeyRef.current = null;
    }

    scrollItemIntoView(listRef.current.querySelector<HTMLButtonElement>('[data-active="true"]'));
  }, [activeItemKey]);

  if (props.items.length === 0) {
    return null;
  }

  const treeItems: TreeListItem[] = props.items.map((item, index) => ({
    active: activeItemKey === getItemKey(item, index),
    key: getItemKey(item, index),
    label: headingByItemKey[getItemKey(item, index)]?.label ?? item.text,
    level: item.depth - 2,
    onClick: () => {
      const key = getItemKey(item, index);
      pendingItemKeyRef.current = key;
      const heading = headingByKeyRef.current.get(key);

      heading?.click();
    },
    tag: props.showHeadingTags === false ? undefined : headingByItemKey[getItemKey(item, index)]?.tag,
    tagVersion: props.showHeadingTags === false ? undefined : headingByItemKey[getItemKey(item, index)]?.tagVersion,
    tone: item.depth === 2 ? TreeListTone.HEADING : TreeListTone.DEFAULT
  }));

  return (
    <S.Root className={props.className}>
      <RailSurfaceWidget label="On this page">
        <TreeListWidget items={treeItems} listRef={listRef} />
      </RailSurfaceWidget>
    </S.Root>
  );
};

const getItemKey = (item: TableOfContentsItem, index: number) => `${index}-${item.depth}-${item.text}`;

const getComparableHeadingText = (text: string) =>
  text
    .trim()
    .replace(/^#+\s*/, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();

namespace S {
  export const Root = styled.aside`
    --tree-list-font-size: 14px;
    --rail-label-font-size: 12px;
    position: sticky;
    top: ${DOCS_RAIL_STICKY_TOP}px;
  `;
}
